import { TextField, TextFieldProps } from 'opensolar-ui'
import PropTypes from 'prop-types'
import { FieldTitle, InputProps, useInput } from 'ra-core'
import { FunctionComponent } from 'react'

import InputHelperText from './InputHelperText'
import sanitizeRestProps from './sanitizeRestProps'

/**
 * Convert Date object to String
 *
 * @param {Date} value value to convert
 * @returns {String} A standardized date (yyyy-MM-dd), to be passed to an <input type="date" />
 */
const convertDateToString = (value: Date) => {
  if (!(value instanceof Date) || isNaN(value.getDate())) return
  const pad = '00'
  const yyyy = value.getFullYear().toString()
  const MM = (value.getMonth() + 1).toString()
  const dd = value.getDate().toString()
  return `${yyyy}-${(pad + MM).slice(-2)}-${(pad + dd).slice(-2)}`
}

const dateRegex = /^\d{4}-\d{2}-\d{2}$/

const getStringFromDate = (value: string | Date) => {
  // null, undefined and empty string values should not go through dateFormatter
  // otherwise, it returns undefined and will make the input an uncontrolled one.
  if (value == null || value === '') {
    return ''
  }

  if (value instanceof Date) {
    return convertDateToString(value)
  }

  // valid dates should not be converted
  if (dateRegex.test(value)) {
    return value
  }

  return convertDateToString(new Date(value))
}

const DateInput: FunctionComponent<InputProps<TextFieldProps> & Omit<TextFieldProps, 'helperText' | 'label'>> = ({
  format = getStringFromDate,
  label,
  options = {},
  source,
  resource,
  helperText,
  margin = 'dense',
  onBlur,
  onChange,
  onFocus,
  parse,
  validate,
  variant = 'outlined',
  ...rest
}) => {
  const {
    id,
    input,
    isRequired,
    meta: { error, touched },
  } = useInput({
    format,
    onBlur,
    onChange,
    onFocus,
    parse,
    resource,
    source,
    validate,
    ...rest,
  })

  return (
    <TextField
      id={id}
      {...input}
      variant={variant}
      margin={margin}
      type="date"
      error={!!(touched && error)}
      helperText={<InputHelperText touched={!!touched} error={error} helperText={helperText} />}
      label={<FieldTitle label={label} source={source} resource={resource} isRequired={isRequired} />}
      InputLabelProps={{
        shrink: true,
      }}
      style={{ margin: 0 }}
      {...options}
      {...sanitizeRestProps(rest)}
    />
  )
}

DateInput.propTypes = {
  label: PropTypes.string,
  options: PropTypes.object,
  resource: PropTypes.string,
  source: PropTypes.string.isRequired,
}

export default DateInput
